import { userRoles } from 'interfaces/user';
import { IUserToken } from 'interfaces/userToken';
import * as rxjs from 'rxjs';
import { Observable } from 'rxjs';
import RxOp from 'rxjs-operators';

import apiService, { ApiService } from './api';
import entityService from './entity';
import selectedVentureService from './profile';
import tokenService, { TokenService } from './token';

type CheckResponse = {
  sessionId: string;
  timeoutOn: Date;
};
export class LoginStepState {
  private _sessionId: rxjs.BehaviorSubject<string | null>;
  private _timeout: rxjs.BehaviorSubject<Date | null>;

  constructor() {
    this._sessionId = new rxjs.BehaviorSubject(null);
    this._timeout = new rxjs.BehaviorSubject(null);
  }

  public get sessionId(): Observable<string | null> {
    return this._sessionId.asObservable();
  }

  public setSessionId(value: string) {
    this._sessionId.next(value);
  }

  clear(): void {
    this._sessionId.next(null);
    this._timeout.next(null);
  }

  public get timeout(): Observable<Date | null> {
    return this._timeout.asObservable();
  }

  public setTimeout(value: Date) {
    this._timeout.next(value);
  }
}
export class AuthService {
  public loginStepState: LoginStepState = new LoginStepState();
  private user$: rxjs.BehaviorSubject<IUserToken>;
  private openChangePassword$: rxjs.BehaviorSubject<boolean>;
  private tokenExpiration$: rxjs.Subject<void>;

  constructor(private api: ApiService, private tokenService: TokenService) {
    this.openChangePassword$ = new rxjs.BehaviorSubject(false);
    this.tokenExpiration$ = new rxjs.Subject();
    this.user$ = new rxjs.BehaviorSubject(this.tokenToUser());
    this.tokenService
      .getToken()
      .pipe(
        RxOp.map((token) => this.tokenToUser(token)),
        RxOp.catchError((err) => {
          console.log('error', err);
          return rxjs.of(null);
        })
      )
      .subscribe((user) => this.user$.next(user));
  }

  public shouldOpenLogin(): rxjs.Observable<boolean> {
    return this.user$.pipe(RxOp.map((user) => !user));
  }

  public login(sessionId: string, code: string) {
    return this.api.post('/auth/login',  { sessionId, code }).pipe(
      this.decideLoginOrPasswordChange(),
      RxOp.catchError((err) => {
        if (err.status === 401) {
          return rxjs.throwError('Usuário ou senha inválidos');
        }
        return rxjs.throwError('Erro ao realizar login');
      })
    );
  }

  public checkEmailAndPass(
    email: string,
    password: string
  ): rxjs.Observable<void> {
    const notificationToken = localStorage.getItem('notificationToken');
    return this.api
      .post<CheckResponse>('/auth/check', {
        email,
        password,
        notificationToken,
      })
      .pipe(RxOp.tap(this.handleStepStateChange), RxOp.mapTo(null));
  }

  public reSendCode(sessionId: string): rxjs.Observable<void> {
    return this.api
      .post<CheckResponse>('/auth/resend-code', { sessionId })
      .pipe(RxOp.tap(this.handleStepStateChange), RxOp.mapTo(null));
  }

  public logout(force: boolean): rxjs.Observable<void> {
    const logoutComplete$ = this.tokenService.clearToken().pipe(
      RxOp.tap(() => selectedVentureService.clearProfile().subscribe()),
      RxOp.tap(() => entityService.cleanEntityGraph().subscribe())
    );

    if (force) this.tokenExpiration$.next();

    return logoutComplete$;
  }

  public onLogout(): rxjs.Observable<void> {
    return this.tokenExpiration$.asObservable();
  }

  public sendResetPassword(email: string): rxjs.Observable<void> {
    return this.api.post('/auth/send-reset', { email });
  }

  public resetPassword(token: string, password: string): rxjs.Observable<void> {
    return this.api.post('/auth/reset-password', { token, password });
  }

  public openChangePassword(): void {
    this.openChangePassword$.next(true);
  }

  public closeChangePassword(): void {
    this.openChangePassword$.next(false);
  }

  public shouldOpenChangePassword(): rxjs.Observable<boolean> {
    return this.openChangePassword$.asObservable();
  }

  public changePassword(
    currentPassword: string,
    newPassword: string
  ): rxjs.Observable<void> {
    return this.api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  public resetPasswordUrl(token: string): Observable<{ url: string }> {
    return this.api.get('auth/reset-password-url', undefined, true, {
      Authorization: `Bearer ${token}`,
    });
  }

  public getUser(): rxjs.Observable<IUserToken> {
    return this.user$.asObservable();
  }

  public isAuthenticated(): rxjs.Observable<boolean> {
    return this.tokenService.getToken().pipe(
      RxOp.first(),
      RxOp.map((token) => !!token)
    );
  }

  /*
    A cada 6 meses tem que mudar a senha,
    Se for Coorporativo, gerenciador, operador e terceiro.
  */
  public needChangePassword(token: string): Observable<boolean> {
    const user = this.tokenService.decode<IUserToken>(token);

    const needChangeRole = [
      userRoles.holding,
      userRoles.venturer,
      userRoles.operator,
      userRoles.third,
    ].some((r) => user.roles.includes(r));

    if (needChangeRole) {
      const dateCheck = new Date(user.updatedPasswordDate);
      dateCheck.setDate(dateCheck.getDate() + 180);
      if (dateCheck < new Date() && user) {
        return rxjs.of(true);
      }
    }
    return rxjs.of(false);
  }

  private tokenToUser(paramToken?: string): IUserToken {
    const token = paramToken ? paramToken : this.tokenService.getTokenSync();
    if (!token) return null;

    const user = this.tokenService.decode<IUserToken>(token);
    if (!user) return null;

    user.fullName = `${user.firstName} ${user.lastName}`;

    user.canAccess = (...roles: string[]) => {
      if (!roles || roles.length === 0) return true;
      if (user.roles.includes('sysAdmin') || user.roles.includes('admin'))
        return true;

      return roles.some((r) => user.roles.includes(r));
    };

    user.isRole = (roles: userRoles[]) =>
      roles.some((r) => user.roles.includes(r));

    return user;
  }

  private decideLoginOrPasswordChange() {
    return RxOp.mergeMap((token: string) => {
      return this.needChangePassword(token).pipe(
        RxOp.mergeMap((need) => {
          if (need) {
            return this.resetPasswordUrl(token).pipe(
              RxOp.tap(({ url }) => {
                window.location.replace(url);
              }),
              RxOp.mergeMap(() => rxjs.throwError('Password change needed'))
            );
          }
          return this.tokenService.setToken(token).pipe(RxOp.first());
        })
      );
    });
  }

  private handleStepStateChange = ({ sessionId, timeoutOn }: CheckResponse) => {
    this.loginStepState.clear();
    this.loginStepState.setSessionId(sessionId);
    this.loginStepState.setTimeout(timeoutOn);
  };
}

const authService = new AuthService(apiService, tokenService);
export default authService;
